import React, { useEffect, useState, useCallback } from 'react';
import { Plus, HandCoins, Check, X as XIcon } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const statusColor: Record<string, string> = { pending: 'warning', approved: 'success', rejected: 'danger' };

export default function Scholarships() {
  const { user } = useAuthStore();
  const isManager = ['admin', 'finance', 'superadmin'].includes(user?.role || '');
  const isStudent = user?.role === 'student';
  const [scholarships, setScholarships] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [aid, setAid] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScholarship, setShowScholarship] = useState(false);
  const [applyFor, setApplyFor] = useState<any>(null);
  const [showAid, setShowAid] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, f] = await Promise.all([
        api.get('/scholarships'),
        api.get('/scholarships/applications'),
        api.get('/scholarships/financial-aid'),
      ]);
      setScholarships(s.data.data || []);
      setApplications(a.data.data || []);
      setAid(f.data.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  const decide = async (id: string, decision: 'approved' | 'rejected') => {
    if (decision === 'rejected' && !window.confirm('Reject this application?')) return;
    try { await api.patch(`/scholarships/applications/${id}/review`, { decision }); toast.success(`Application ${decision}`); doFetch(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Scholarships & Financial Aid</h1><p className="page-subtitle">{isStudent ? 'Available scholarships and your applications' : 'Manage scholarships, applications, and bursaries'}</p></div>
        {isManager && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => setShowAid(true)}><Plus size={16} />Record Aid</button>
            <button className="btn btn-primary" onClick={() => setShowScholarship(true)}><Plus size={16} />New Scholarship</button>
          </div>
        )}
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Scholarships</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
        {loading ? <div className="loader loader-dark" /> : scholarships.length === 0 ? (
          <div className="card"><div className="empty-state"><HandCoins size={40} /><p>No scholarships set up yet</p></div></div>
        ) : scholarships.map((s: any) => (
          <div key={s.id} className="card">
            <div style={{ fontWeight: 700 }}>{s.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.sponsor || 'Institution-funded'} · {s.coverage_type}</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--success)', marginTop: 8 }}>KES {Number(s.amount).toLocaleString()}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{s.awarded_count} awarded</div>
            {isStudent && <button className="btn btn-primary btn-sm" style={{ marginTop: 10, width: '100%' }} onClick={() => setApplyFor(s)}>Apply</button>}
          </div>
        ))}
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>{isStudent ? 'My Applications' : 'Applications'}</h3>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="table-container">
          <table>
            <thead><tr>{!isStudent && <th>Student</th>}<th>Scholarship</th><th>Amount</th><th>Status</th>{isManager && <th>Actions</th>}</tr></thead>
            <tbody>
              {applications.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No applications yet</p></div></td></tr>
              : applications.map((a: any) => (
                <tr key={a.id}>
                  {!isStudent && <td>{a.student_first_name} {a.student_last_name}</td>}
                  <td style={{ fontWeight: 500 }}>{a.scholarship_name}</td>
                  <td>KES {Number(a.amount).toLocaleString()}</td>
                  <td><span className={`badge badge-${statusColor[a.status]}`} style={{ textTransform: 'capitalize' }}>{a.status}</span></td>
                  {isManager && (
                    <td>
                      {a.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(a.id, 'approved')}><Check size={13} />Approve</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => decide(a.id, 'rejected')}><XIcon size={13} />Reject</button>
                        </div>
                      ) : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isManager && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Financial Aid (Bursaries & Loans)</h3>
          <div className="card">
            <div className="table-container">
              <table>
                <thead><tr><th>Student</th><th>Type</th><th>Source</th><th>Amount</th><th>Disbursed</th></tr></thead>
                <tbody>
                  {aid.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><p>No financial aid recorded yet</p></div></td></tr>
                  : aid.map((f: any) => (
                    <tr key={f.id}>
                      <td>{f.student_first_name} {f.student_last_name}</td>
                      <td style={{ textTransform: 'capitalize' }}>{f.aid_type}</td>
                      <td>{f.source || '—'}</td>
                      <td>KES {Number(f.amount).toLocaleString()}</td>
                      <td><span className={`badge badge-${f.disbursed ? 'success' : 'gray'}`}>{f.disbursed ? 'Yes' : 'Pending'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {isStudent && aid.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '24px 0 12px' }}>My Financial Aid</h3>
          <div className="card">
            <div className="table-container">
              <table>
                <thead><tr><th>Type</th><th>Source</th><th>Amount</th><th>Disbursed</th></tr></thead>
                <tbody>
                  {aid.map((f: any) => (
                    <tr key={f.id}>
                      <td style={{ textTransform: 'capitalize' }}>{f.aid_type}</td>
                      <td>{f.source || '—'}</td>
                      <td>KES {Number(f.amount).toLocaleString()}</td>
                      <td><span className={`badge badge-${f.disbursed ? 'success' : 'gray'}`}>{f.disbursed ? 'Yes' : 'Pending'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showScholarship && <NewScholarshipModal onClose={() => setShowScholarship(false)} onSaved={() => { setShowScholarship(false); doFetch(); }} />}
      {applyFor && <ApplyModal scholarship={applyFor} onClose={() => setApplyFor(null)} onSaved={() => { setApplyFor(null); doFetch(); }} />}
      {showAid && <RecordAidModal onClose={() => setShowAid(false)} onSaved={() => { setShowAid(false); doFetch(); }} />}
    </div>
  );
}

function NewScholarshipModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ coverage_type: 'partial' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.amount) { toast.error('Name and amount are required'); return; }
    setLoading(true);
    try { await api.post('/scholarships', form); toast.success('Scholarship created'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>New Scholarship</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Sponsor</label><input className="form-input" placeholder="e.g. County Government, CDF" value={form.sponsor || ''} onChange={e => set('sponsor', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input type="number" className="form-input" required value={form.amount || ''} onChange={e => set('amount', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Coverage</label><select className="form-input form-select" value={form.coverage_type} onChange={e => set('coverage_type', e.target.value)}><option value="partial">Partial</option><option value="full">Full</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">Academic Year</label><input className="form-input" placeholder="2024/2025" value={form.academic_year || ''} onChange={e => set('academic_year', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Create'}</button></div>
        </form>
      </div>
    </div>
  );
}

function ApplyModal({ scholarship, onClose, onSaved }: any) {
  const [justification, setJustification] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post('/scholarships/apply', { scholarship_id: scholarship.id, justification }); toast.success('Application submitted'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Apply — {scholarship.name}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Why do you need this scholarship?</label><textarea className="form-input" rows={4} value={justification} onChange={e => setJustification(e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit Application'}</button></div>
        </form>
      </div>
    </div>
  );
}

function RecordAidModal({ onClose, onSaved }: any) {
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ aid_type: 'bursary', disbursed: false });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id || !form.amount) { toast.error('Student and amount are required'); return; }
    setLoading(true);
    try { await api.post('/scholarships/financial-aid', form); toast.success('Financial aid recorded'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Record Financial Aid</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Type *</label><select className="form-input form-select" value={form.aid_type} onChange={e => set('aid_type', e.target.value)}><option value="bursary">Bursary</option><option value="loan">Loan</option><option value="grant">Grant</option></select></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input type="number" className="form-input" required value={form.amount || ''} onChange={e => set('amount', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Source</label><input className="form-input" placeholder="e.g. HELB, County Bursary" value={form.source || ''} onChange={e => set('source', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Academic Year</label><input className="form-input" placeholder="2024/2025" value={form.academic_year || ''} onChange={e => set('academic_year', e.target.value)} /></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><input type="checkbox" checked={!!form.disbursed} onChange={e => set('disbursed', e.target.checked)} />Already disbursed — credit to fee balance now</label>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Record'}</button></div>
        </form>
      </div>
    </div>
  );
}
